import { useState } from "react";
import { Badge, Card, CardSection, Checkbox, Slider, UploadField, resolveAssetUrl } from "@/components/ui";
import { eventsApi } from "@/lib/api/endpoints";
import { useBuilder } from "../BuilderContext";
import { useToast } from "@/components/ui";
import { SnapshotFilters } from "./SnapshotTab";
import { cn } from "@/lib/utils/cn";
import { SNAPSHOT_FRAME_PRESETS, snapshotFramePresetDataUrl } from "@/lib/utils/snapshotFramePresets";
import {
  IconCamera, IconCheck, IconEye, IconLink, IconPrinter, IconSparkles,
} from "../components/icons";

/**
 * The guest's journey, in one place.
 *
 * ## Why this tab exists
 *
 * The single biggest lever on a booth is *how many screens stand between a
 * guest and their photo*. Yet every control that adds or removes a screen used
 * to be scattered: the mode toggles were in General, "skip participants" was in
 * a different section of General, snapshot filters had a tab of their own, and
 * the QR — now the only route to payment — was buried at the bottom of Frame &
 * QR. Nowhere could you see the journey you'd actually built.
 *
 * So an operator would turn on both modes "to give people options" without ever
 * seeing that they'd inserted a decision screen at the moment attention is most
 * fragile.
 *
 * This tab shows the flow as a list, greys out the steps that won't run, and
 * puts every switch that changes it right there. If a screen appears here, a
 * guest sees it.
 */

const SERVICES = [
  {
    key: "ai_photo_booth" as const,
    icon: <IconSparkles size={16} />,
    title: "AI photo",
    description: "Takes 30–60 seconds per guest. The reason people come, and the reason queues form.",
  },
  {
    key: "snapshot" as const,
    icon: <IconCamera size={16} />,
    title: "Snapshot",
    description: "Instant filtered photo, no AI wait. This is your throughput lever.",
  },
  {
    key: "print_photo" as const,
    icon: <IconPrinter size={16} />,
    title: "Print",
    description: "Adds Print as something a guest can buy from their phone. Needs a printer configured in Hardware.",
  },
];

const SNAPSHOT_LAYOUTS = [
  { id: "single", name: "Classic portrait", photos: 1, shape: "one" },
  { id: "strip_2", name: "Double strip", photos: 2, shape: "two" },
  { id: "strip_3", name: "Three-photo strip", photos: 3, shape: "three" },
  { id: "strip_4", name: "Four-photo strip", photos: 4, shape: "four" },
  { id: "grid_4", name: "Four-photo collage", photos: 4, shape: "grid" },
];

export function FlowTab() {
  const { draft, update, event, setEvent, patchEvent, markPreviewStale } = useBuilder();
  const toast = useToast();
  const [frameCategory, setFrameCategory] = useState("All");

  const snapshot = draft.snapshot ?? {};
  const skipParticipants = draft.workflow?.skip_participants ?? false;
  const skipVibes = draft.workflow?.skip_vibes ?? false;
  const qrEnabled = draft.event?.qr_code?.enabled !== false;

  const aiOn = Boolean(event.ai_photo_booth);
  const snapshotOn = Boolean(event.snapshot);
  const bothModes = aiOn && snapshotOn;

  const setSnapshot = (patch: Partial<typeof snapshot>) =>
    update((current) => ({ ...current, snapshot: { ...current.snapshot, ...patch } }));

  const frameCategories = ["All", ...new Set(SNAPSHOT_FRAME_PRESETS.map((preset) => preset.category))];
  const visibleFramePresets = frameCategory === "All"
    ? SNAPSHOT_FRAME_PRESETS
    : SNAPSHOT_FRAME_PRESETS.filter((preset) => preset.category === frameCategory);

  const setWorkflow = (skip: boolean) =>
    update((current) => ({
      ...current,
      workflow: { ...current.workflow, skip_participants: skip },
    }));

  const setSkipVibes = (skip: boolean) =>
    update((current) => ({
      ...current,
      workflow: { ...current.workflow, skip_vibes: skip },
    }));

  // Service flags are columns on the event record, not config keys — so they
  // save through a different channel from everything else on this tab.
  async function toggleService(key: (typeof SERVICES)[number]["key"]) {
    try {
      const updated = await eventsApi.update(event.id, { [key]: !event[key] });
      setEvent({ ...event, ...updated });
      markPreviewStale();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not save mode setting");
    }
  }

  // The journey as the guest will actually walk it.
  const steps = [
    { label: "Welcome", detail: "Branding tab", on: true },
    {
      label: "Choose AI or Snapshot",
      detail: bothModes ? "shown — both modes are on" : "skipped — only one mode is on",
      on: bothModes,
    },
    {
      label: "Choose a participant",
      detail: skipParticipants ? "skipped" : "shown — Content tab fills it",
      on: !skipParticipants,
    },
    { label: "Choose a vibe", detail: skipVibes ? "skipped" : "Content tab", on: !skipVibes },
    { label: "Camera", detail: "Hardware + Output tabs", on: true },
    { label: "AI processing", detail: aiOn ? "AI photos only" : "never — AI is off", on: aiOn },
    { label: "Result + QR", detail: qrEnabled ? "QR shown" : "no QR — guests cannot pay", on: true },
    { label: "Their phone: pay & save", detail: "Pricing tab", on: qrEnabled },
  ];

  const screenCount = steps.filter((step) => step.on).length;

  return (
    <div className="flex flex-col gap-8">
      <CardSection
        eyebrow="Journey"
        title="What a guest walks through"
        description="Every step below is a moment someone can walk away. Fewer is almost always better."
      >
        <Card className="flex flex-col gap-4 p-5">
          <div className="flex items-center gap-2">
            <Badge tone={screenCount <= 6 ? "ok" : "warn"}>{screenCount} screens</Badge>
            <span className="text-xs text-haze-400">
              {screenCount <= 6
                ? "Lean. Good for a queue."
                : "Every optional step is somewhere to lose people — check each one earns its place."}
            </span>
          </div>

          {/* Numbering counts only the frames that actually run, so "04 of 06"
              means what it says. Numbering the skipped ones would tell an
              operator their booth has eight screens when it has six. */}
          <ol className="flex flex-col divide-y divide-ink-600/60">
            {(() => {
              let frame = 0;
              return steps.map((step) => {
                if (step.on) frame += 1;

                // A skipped step is context, not content: one muted line, no
                // heading weight. It was competing with the real steps for
                // attention while describing something that never happens.
                if (!step.on) {
                  return (
                    <li key={step.label} className="flex items-baseline gap-3 py-1.5 text-xs text-haze-400">
                      <span className="w-5 shrink-0 font-mono tabular-nums">––</span>
                      <span>{step.label} — {step.detail}</span>
                    </li>
                  );
                }

                return (
                  <li key={step.label} className="flex items-baseline gap-3 py-2.5">
                    <span className="w-5 shrink-0 font-mono text-2xs tabular-nums text-accent">
                      {String(frame).padStart(2, "0")}
                    </span>
                    <span className="flex flex-col gap-0.5">
                      <span className="text-sm font-semibold text-haze-100">{step.label}</span>
                      <span className="text-xs text-haze-400">{step.detail}</span>
                    </span>
                  </li>
                );
              });
            })()}
          </ol>
        </Card>
      </CardSection>

      <CardSection
        eyebrow="Modes"
        title="What the booth can do"
        description="Turning on two capture modes adds a choice screen. Turn on only the one you want people to use."
        >
        <Card className="flex flex-col gap-4 p-5">
            <div className="grid gap-3 sm:grid-cols-3">
              {SERVICES.map((service) => {
                const enabled = Boolean(event[service.key]);
                return (
                  <button
                    key={service.key}
                    onClick={() => toggleService(service.key)}
                    className={cn(
                      "flex flex-col gap-2 rounded-xl border p-4 text-left transition-colors",
                      enabled ? "border-accent/60 bg-accent/[0.06]" : "border-ink-600 bg-ink-750 hover:border-ink-500",
                    )}
                  >
                    <span className="flex items-center justify-between">
                      <span className={cn("grid h-8 w-8 place-items-center rounded-lg",
                        enabled ? "bg-accent text-accent-ink" : "bg-ink-700 text-haze-300")}>
                        {service.icon}
                      </span>
                      {enabled && <IconCheck size={14} className="text-accent" />}
                    </span>
                    <span className="text-sm font-semibold text-haze-100">{service.title}</span>
                    <span className="text-xs leading-relaxed text-haze-400">{service.description}</span>
                  </button>
                );
              })}
            </div>

            {bothModes && (
              <p className="rounded-lg border border-warn/40 bg-warn/5 px-3 py-2 text-xs leading-relaxed text-warn">
                Both capture modes are on, so guests get a screen asking which one
                they want. If you know which you'd rather they used, turn the other
                off and remove the decision.
              </p>
            )}

            {!aiOn && !snapshotOn && (
              <p className="rounded-lg border border-danger/40 bg-danger/5 px-3 py-2 text-xs leading-relaxed text-danger">
                No capture mode is on. Guests can open the booth but cannot take a photo.
              </p>
            )}
        </Card>
      </CardSection>

      <CardSection
        eyebrow="Participants"
        title="Do guests pick a side?"
        description="Clubs, stages, wedding sides, house teams. Skip it when there's nothing meaningful to choose."
        >
        <Card className="p-5">
            <Checkbox
              label="Skip the participant screen"
              hint="Turn this on for a single-brand booth. A screen with one option on it is a screen guests tap through for no reason."
              checked={skipParticipants}
              onChange={setWorkflow}
            />
        </Card>
      </CardSection>

      <CardSection
        eyebrow="Vibes"
        title="Do guests choose a vibe?"
        description="Turn this off for a direct participant-only journey, such as a Kids Booth where the dream job defines the experience."
      >
        <Card className="p-5">
          <Checkbox
            label="Skip the vibe screen"
            hint="After choosing a participant or dream job, guests go straight to the camera."
            checked={skipVibes}
            onChange={setSkipVibes}
          />
        </Card>
      </CardSection>

      <CardSection
        eyebrow="Snapshot"
        title="Filters and retakes"
        description="Only used when Snapshot is on. Filters are applied for real when the photo is produced, so the print matches the preview."
        >
        <Card className="flex flex-col gap-6 p-5">
            {!snapshotOn && (
              <p className="rounded-lg border border-ink-600 bg-ink-800/60 px-3 py-2 text-xs text-haze-400">
                Snapshot is currently off, so none of this reaches a guest.
              </p>
            )}

            <Slider
              label="Retakes per session"
              min={0} max={5} step={1}
              value={snapshot.free_retakes ?? 2}
              onChange={(value) => setSnapshot({ free_retakes: value })}
              hint="The guest may retake one photo or restart the whole set. Each retake uses one from this shared allowance."
            />

            <Slider
              label="Capture countdown (seconds)"
              min={0} max={10} step={1}
              value={snapshot.countdown_seconds ?? 3}
              onChange={(value) => setSnapshot({ countdown_seconds: value })}
              hint="Zero takes the photo immediately. Otherwise each photo counts down before capture."
            />

            <div className="flex flex-col gap-3">
              <div>
                <h3 className="text-sm font-semibold text-haze-100">Layouts guests can choose</h3>
                <p className="mt-1 text-xs text-haze-400">Each completed layout is delivered as one composed image.</p>
              </div>
              <div className="grid gap-2 sm:grid-cols-2">
                {SNAPSHOT_LAYOUTS.map((layout) => {
                  const selected = (snapshot.layouts ?? ["single"]).includes(layout.id);
                  return (
                    <button
                      key={layout.id}
                      type="button"
                      aria-pressed={selected}
                      onClick={() => {
                        const current = snapshot.layouts ?? ["single"];
                        const next = selected
                          ? current.filter((id) => id !== layout.id)
                          : [...current, layout.id];
                        setSnapshot({ layouts: next.length ? next : [layout.id] });
                      }}
                      className={cn(
                        "flex items-center gap-3 rounded-xl border p-3 text-left transition-colors",
                        selected ? "border-accent/60 bg-accent/[0.06]" : "border-ink-600 hover:border-ink-500",
                      )}
                    >
                      <LayoutGlyph shape={layout.shape} />
                      <span className="flex flex-col">
                        <span className="text-xs font-semibold text-haze-100">{layout.name}</span>
                        <span className="text-2xs text-haze-400">{layout.photos} photo{layout.photos === 1 ? "" : "s"}</span>
                      </span>
                      {selected && <IconCheck size={14} className="ml-auto text-accent" />}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex flex-col gap-3">
              <div>
                <h3 className="text-sm font-semibold text-haze-100">Choose a starter frame design</h3>
                <p className="mt-1 text-xs text-haze-400">
                  Choose a coordinated style for your snapshot layouts. Upload custom transparent artwork
                  below for any layout to override the starter design. Snapshot frames never affect AI photos.
                </p>
              </div>
              <div className="flex gap-2 overflow-x-auto pb-1">
                {frameCategories.map((category) => <button key={category} type="button" onClick={() => setFrameCategory(category)}
                  className={cn("min-h-10 shrink-0 rounded-full border px-4 py-2 text-xs font-semibold transition-colors",
                    frameCategory === category ? "border-accent bg-accent text-accent-ink" : "border-ink-600 text-haze-300 hover:border-ink-500")}>
                  {category}
                </button>)}
              </div>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {visibleFramePresets.map((preset) => {
                  const selected = snapshot.frame_preset === preset.slug;
                  return <button key={preset.slug} type="button" aria-pressed={selected}
                    onClick={() => { setSnapshot({ frame_preset: preset.slug }); markPreviewStale(); }}
                    className={cn("flex items-center gap-3 rounded-xl border p-3 text-left transition-colors",
                      selected ? "border-accent bg-accent/[0.06]" : "border-ink-600 hover:border-ink-500")}>
                    <span className="relative block h-28 w-[4.5rem] shrink-0 overflow-hidden rounded-lg bg-gradient-to-br from-amber-100 via-rose-100 to-slate-300">
                      <span className="absolute inset-[18%_12%] rounded bg-slate-700/40" />
                      <span className="absolute inset-0">
                        <img src={snapshotFramePresetDataUrl("single", preset.slug) ?? ""} alt="" className="h-full w-full object-fill" />
                      </span>
                    </span>
                    <span className="min-w-0">
                      <span className="block text-2xs uppercase tracking-wider text-haze-400">{preset.category}</span>
                      <span className="mt-1 block text-sm font-semibold text-haze-100">{preset.name}</span>
                      {selected && <span className="mt-2 inline-flex items-center gap-1 text-xs text-accent"><IconCheck size={13} /> Selected</span>}
                    </span>
                  </button>;
                })}
              </div>
              <button type="button" onClick={() => { setSnapshot({ frame_preset: "" }); markPreviewStale(); }}
                className="self-start text-xs text-haze-400 underline underline-offset-2 hover:text-haze-100">
                Use plain frame instead
              </button>
              <div className="grid gap-4 border-t border-ink-700 pt-4 sm:grid-cols-2">
                {SNAPSHOT_LAYOUTS.map((layout) => {
                  const uploaded = snapshot.layout_frames?.[layout.id];
                  const preview = uploaded ? resolveAssetUrl(uploaded) : snapshotFramePresetDataUrl(layout.id, snapshot.frame_preset ?? "");
                  return <UploadField
                    key={layout.id}
                    label={`${layout.name} custom frame`}
                    value={uploaded}
                    previewSrc={uploaded ? undefined : preview ?? undefined}
                    onChange={(value) => setSnapshot({
                      layout_frames: { ...snapshot.layout_frames, [layout.id]: value ?? "" },
                    })}
                    accept="image/png,image/webp,image/jpeg"
                    hint="Upload a transparent PNG, WebP or JPEG. SVG uploads are served as downloads and cannot be previewed or composited in the booth."
                  />;
                })}
              </div>
            </div>

            <div>
              <SnapshotFilters
                selected={snapshot.filters ?? ["none", "mono", "film", "warm", "vivid"]}
                onChange={(filters) => setSnapshot({ filters })}
              />
            </div>
        </Card>
      </CardSection>

      <CardSection
        eyebrow="Handoff"
        title="The QR on the result screen"
        description="How a guest gets their photo. Since payment moved to their phone, this is the only route to a sale."
        >
        <Card className="flex flex-col gap-4 p-5">
            <Checkbox
              label="Show the QR code"
              hint="Turning this off means guests have no way to download, print or pay for anything. Only do it for a booth that gives photos away on a screen."
              checked={qrEnabled}
              onChange={(checked) =>
                patchEvent({ qr_code: { ...draft.event?.qr_code, enabled: checked } })
              }
            />

            {!qrEnabled && (
              <p className="rounded-lg border border-danger/40 bg-danger/5 px-3 py-2 text-xs leading-relaxed text-danger">
                <IconLink size={12} className="mr-1 inline" />
                With no QR there is no way for a guest to pay or keep their photo.
                Everything in Pricing is unreachable.
              </p>
            )}

            <p className="flex items-start gap-2 text-xs leading-relaxed text-haze-400">
              <IconEye size={13} className="mt-0.5 shrink-0" />
              Position, label and whether the code is burned into prints are on the
              Output tab, with the frame.
            </p>
        </Card>
      </CardSection>
    </div>
  );
}

function LayoutGlyph({ shape }: { shape: string }) {
  const cells = shape === "one" ? 1 : shape === "two" ? 2 : shape === "three" ? 3 : 4;
  return (
    <span className={cn(
      "grid h-11 w-8 shrink-0 gap-0.5 rounded border border-ink-500 bg-ink-850 p-1",
      shape === "grid" ? "grid-cols-2 grid-rows-2" : "grid-cols-1",
    )}>
      {Array.from({ length: cells }, (_, index) => (
        <span key={index} className="rounded-[2px] bg-accent/70" />
      ))}
    </span>
  );
}
